import './applicantEditIncomePage.scss';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import red from '@mui/material/colors/red';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from '@mui/material/TextField';
import { isEmpty, toString, uniqueId } from 'lodash';
import { useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import { additionalIncomeTypeOptions } from '../../../store/applicantStore/constants/additionalIncomeTypeOptions';
import { incomeFrequencyOptions } from '../../../store/applicantStore/constants/incomeFrequencyOptions';
import { incomeTypeOptions } from '../../../store/applicantStore/constants/incomeTypeOptions';
import { IncomeTypeEnum } from '../../../store/applicantStore/enums/IncomeTypeEnum';
import { YES } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: 'onBlur',
    criteriaMode: 'firstError',
    delayError: 200,
  });

  const {
    additionalIncomeAmount,
    additionalIncomeDurationMonths,
    additionalIncomeDurationYears,
    additionalIncomeEmployerName,
    additionalIncomeFrequency,
    additionalIncomeJobTitle,
    additionalIncomeType,
    computed: { reviewEditPathname, reviewEditProgress },
    hasAdditionalIncome,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setAdditionalIncomeAmount,
    setAdditionalIncomeDurationMonths,
    setAdditionalIncomeDurationYears,
    setAdditionalIncomeEmployerName,
    setAdditionalIncomeFrequency,
    setAdditionalIncomeJobTitle,
    setAdditionalIncomeType,
    setHasAdditionalIncome,
    syncIncome,
  } = useApplicantStore();

  const shouldShowIncomeFields =
    watch('hasAdditionalIncomeValue', hasAdditionalIncome) === YES;

  const additionalIncomeValidation = {
    required: t('validation.additionalIncome.required'),
  };

  const incomeTypeValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeType.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeFrequencyValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.incomeFrequency.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeAmountValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      setValueAs: (value: string): string =>
        inputEventHandler.currency.parser(value),
      required: t('validation.incomeAmount.required'),
      min: {
        value: 0,
        message: t('validation.incomeAmount.min', { min: '$0' }),
      },
    };
  }, [shouldShowIncomeFields]);

  const incomeEmployerNameValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.employerName.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeJobTitleValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.jobTitle.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationYearsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationYears.required'),
    };
  }, [shouldShowIncomeFields]);

  const incomeDurationMonthsValidation = useMemo(() => {
    if (!shouldShowIncomeFields) {
      return {};
    }

    return {
      required: t('validation.durationMonths.required'),
    };
  }, [shouldShowIncomeFields]);

  const showSecondaryEmploymentFields =
    watch('incomeTypeValue', additionalIncomeType) ===
    IncomeTypeEnum.SECONDARY_EMPLOYMENT;

  const finishEditing = (): void => {
    navigate(reviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      hasAdditionalIncomeValue,
      incomeAmountValue,
      incomeDurationMonthsValue,
      incomeDurationYearsValue,
      incomeEmployerNameValue,
      incomeFrequencyValue,
      incomeJobTitleValue,
      incomeTypeValue,
    } = values;

    setHasAdditionalIncome(hasAdditionalIncomeValue);

    if (shouldShowIncomeFields) {
      setAdditionalIncomeAmount(incomeAmountValue);
      setAdditionalIncomeFrequency(incomeFrequencyValue);
      setAdditionalIncomeType(incomeTypeValue);

      if (showSecondaryEmploymentFields) {
        setAdditionalIncomeDurationMonths(incomeDurationMonthsValue);
        setAdditionalIncomeDurationYears(incomeDurationYearsValue);
        setAdditionalIncomeEmployerName(incomeEmployerNameValue);
        setAdditionalIncomeJobTitle(incomeJobTitleValue);
      } else {
        setAdditionalIncomeDurationMonths(null);
        setAdditionalIncomeDurationYears(null);
        setAdditionalIncomeEmployerName(null);
        setAdditionalIncomeJobTitle(null);
      }
    } else {
      setAdditionalIncomeAmount(null);
      setAdditionalIncomeFrequency(null);
      setAdditionalIncomeType(null);
      setAdditionalIncomeDurationMonths(null);
      setAdditionalIncomeDurationYears(null);
      setAdditionalIncomeEmployerName(null);
      setAdditionalIncomeJobTitle(null);
    }

    if (isDirty) {
      const synced = await syncIncome();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-edit-income-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantEditIncome.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantEditIncome.header.previousStepButton
            }
            type="button"
            onClick={finishEditing}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={locator.pages.applicantEditIncome.header.drawerButton}
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantEditIncome.progress.root}
            value={reviewEditProgress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantEditIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantEditIncome.form.h1}>
                {t('ApplicantEditIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantEditIncome.form.h2}>
                {t('ApplicantEditIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.applicantEditIncome.alert.error}
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div>
                  <TextField
                    {...register(
                      'hasAdditionalIncomeValue',
                      additionalIncomeValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    error={!isEmpty(errors.hasAdditionalIncomeValue)}
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditIncome.form
                          .hasAdditionalIncomeSelect,
                    }}
                    label={t('ApplicantEditIncomePage.additionalIncome')}
                    value={watch(
                      'hasAdditionalIncomeValue',
                      hasAdditionalIncome || '',
                    )}
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantEditIncomePage.additionalIncome')}
                    </MenuItem>

                    {additionalIncomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.hasAdditionalIncomeValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditIncome.form
                          .hasAdditionalIncomeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.hasAdditionalIncomeValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeTypeValue', incomeTypeValidation)}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields && !isEmpty(errors.incomeTypeValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditIncome.form.incomeTypeSelect,
                    }}
                    label={t('ApplicantEditIncomePage.incomeType')}
                    value={
                      shouldShowIncomeFields
                        ? watch(
                            'incomeTypeValue',
                            additionalIncomeType || '',
                          ) || ''
                        : ''
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantEditIncomePage.incomeType')}
                    </MenuItem>

                    {incomeTypeOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={uniqueId(value)} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeTypeValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditIncome.form
                          .incomeTypeSelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeTypeValue.message)}
                    </span>
                  )}
                </div>

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeEmployerNameValue',
                        incomeEmployerNameValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.employerNameValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applicantEditIncome.form
                            .employerNameInput,
                      }}
                      label={t('ApplicantEditIncomePage.employerName')}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeEmployerNameValue',
                              additionalIncomeEmployerName || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeEmployerNameValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantEditIncome.form
                            .employerNameInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeEmployerNameValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <TextField
                      {...register(
                        'incomeJobTitleValue',
                        incomeJobTitleValidation,
                      )}
                      disabled={!shouldShowIncomeFields}
                      error={!isEmpty(errors.jobTitleValue)}
                      inputProps={{
                        'data-testid':
                          locator.pages.applicantEditIncome.form.jobTitleInput,
                      }}
                      label={t('ApplicantEditIncomePage.jobTitle')}
                      value={
                        shouldShowIncomeFields
                          ? watch(
                              'incomeJobTitleValue',
                              additionalIncomeJobTitle || '',
                            ) || ''
                          : ''
                      }
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.incomeJobTitleValue) && (
                      <span
                        data-testid={
                          locator.pages.applicantEditIncome.form
                            .jobTitleInputError
                        }
                        style={{ fontSize: '12px', color: red[700] }}
                      >
                        {toString(errors.incomeJobTitleValue.message)}
                      </span>
                    )}
                  </div>
                )}

                {showSecondaryEmploymentFields && (
                  <div
                    className="inline-inputs"
                    style={{ marginTop: '1.5rem' }}
                  >
                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationYearsValue',
                          incomeDurationYearsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        disabled={!shouldShowIncomeFields}
                        error={!isEmpty(errors.durationYearsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditIncome.form.yearSelect,
                        }}
                        label={t('ApplicantEditIncomePage.durationYears')}
                        value={
                          shouldShowIncomeFields
                            ? watch(
                                'incomeDurationYearsValue',
                                additionalIncomeDurationYears,
                              ) ?? ''
                            : ''
                        }
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantEditIncomePage.durationYears')}
                        </MenuItem>

                        {Array.from({ length: 11 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-years-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationYearsValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantEditIncome.form
                              .yearSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationYearsValue.message)}
                        </span>
                      )}
                    </div>

                    <div className="input-half">
                      <TextField
                        {...register(
                          'incomeDurationMonthsValue',
                          incomeDurationMonthsValidation,
                        )}
                        SelectProps={{
                          IconComponent: ExpandMoreRoundedIcon,
                        }}
                        disabled={!shouldShowIncomeFields}
                        error={!isEmpty(errors.durationMonthsValue)}
                        inputProps={{
                          'data-testid':
                            locator.pages.applicantEditIncome.form.monthSelect,
                        }}
                        label={t('ApplicantEditIncomePage.durationMonths')}
                        value={
                          shouldShowIncomeFields
                            ? watch(
                                'incomeDurationMonthsValue',
                                additionalIncomeDurationMonths,
                              ) ?? ''
                            : ''
                        }
                        fullWidth
                        select
                      >
                        <MenuItem value="" disabled>
                          {t('ApplicantEditIncomePage.durationMonths')}
                        </MenuItem>

                        {Array.from({ length: 12 }, (_, index) => {
                          return (
                            <MenuItem
                              key={uniqueId(`duration-months-${index}`)}
                              value={index}
                            >
                              {index}
                            </MenuItem>
                          );
                        })}
                      </TextField>

                      {!isEmpty(errors.incomeDurationMonthsValue) && (
                        <span
                          data-testid={
                            locator.pages.applicantEditIncome.form
                              .monthSelectError
                          }
                          style={{ fontSize: '12px', color: red[700] }}
                        >
                          {toString(errors.incomeDurationMonthsValue.message)}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'incomeFrequencyValue',
                      incomeFrequencyValidation,
                    )}
                    SelectProps={{
                      IconComponent: ExpandMoreRoundedIcon,
                    }}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeFrequencyValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditIncome.form.frequencySelect,
                    }}
                    label={t('ApplicantEditIncomePage.incomeFrequency')}
                    value={
                      shouldShowIncomeFields
                        ? watch(
                            'incomeFrequencyValue',
                            additionalIncomeFrequency || '',
                          ) || ''
                        : ''
                    }
                    fullWidth
                    select
                  >
                    <MenuItem value="" disabled>
                      {t('ApplicantEditIncomePage.incomeFrequency')}
                    </MenuItem>

                    {incomeFrequencyOptions.map(({ name, value }) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>

                  {!isEmpty(errors.incomeFrequencyValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditIncome.form
                          .frequencySelectError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeFrequencyValue.message)}
                    </span>
                  )}
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register('incomeAmountValue', incomeAmountValidation)}
                    disabled={!shouldShowIncomeFields}
                    error={
                      shouldShowIncomeFields &&
                      !isEmpty(errors.incomeAmountValue)
                    }
                    inputProps={{
                      'data-testid':
                        locator.pages.applicantEditIncome.form.amountInput,
                      inputMode: 'numeric',
                    }}
                    label={t('ApplicantEditIncomePage.incomeAmount')}
                    type="text"
                    value={
                      shouldShowIncomeFields
                        ? inputEventHandler.currency.formatter(
                            watch('incomeAmountValue', additionalIncomeAmount),
                          )
                        : ''
                    }
                    variant="outlined"
                    fullWidth
                    onBlur={inputEventHandler.currency.blur(
                      'incomeAmountValue',
                      setValue,
                      trigger,
                    )}
                    onChange={inputEventHandler.currency.change(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onFocus={inputEventHandler.currency.focus(
                      'incomeAmountValue',
                      setValue,
                    )}
                    onKeyDown={inputEventHandler.number.keydown}
                    onPasteCapture={inputEventHandler.number.pasteCapture}
                  />

                  {!isEmpty(errors.incomeAmountValue) && (
                    <span
                      data-testid={
                        locator.pages.applicantEditIncome.form.amountInputError
                      }
                      style={{ fontSize: '12px', color: red[700] }}
                    >
                      {toString(errors.incomeAmountValue.message)}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <LoadingButton
                    color="secondary"
                    data-testid={
                      locator.pages.applicantEditIncome.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('ApplicantEditIncomePage.cancel')}
                  </LoadingButton>

                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applicantEditIncome.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingIncome}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplicantEditIncomePage.save')}
                  </LoadingButton>
                </div>
              </div>
            </div>
            <footer data-testid={locator.pages.applicantEditIncome.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantEditIncomePage';
